import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiClientError } from '@/lib/api/client'
import {
  fetchManagerialDashboardShareCandidates,
  fetchManagerialDashboardShares,
  grantManagerialDashboardShare,
  revokeManagerialDashboardShare,
  updateManagerialDashboardShareExpiry,
} from '@/lib/api/managerial-dashboard-shares-admin.service'
import { resolveManagerialDashboardError } from '../lib/managerial-dashboard'
import type { ManagerialDashboardShare, ManagerialDashboardShareCandidate } from '@/types/api'

export const SHARE_LOAD_ERROR = 'Não foi possível carregar os acessos compartilhados.'
export const SHARE_SAVE_SUCCESS = 'Acesso à Dash Gerencial atualizado.'
export const SHARE_REVOKE_SUCCESS = 'Acesso à Dash Gerencial revogado.'

const SEARCH_DEBOUNCE_MS = 300

function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true
  if (error instanceof Error && (error.name === 'AbortError' || /abort/i.test(error.message))) {
    return true
  }
  return false
}

function isShare(value: unknown): value is ManagerialDashboardShare {
  if (typeof value !== 'object' || value === null) return false
  const item = value as Partial<ManagerialDashboardShare>
  return (
    typeof item.id === 'number' &&
    typeof item.institution_id === 'number' &&
    typeof item.institution_name === 'string' &&
    typeof item.institution_email === 'string' &&
    typeof item.expires_on === 'string' &&
    typeof item.granted_at === 'string'
  )
}

function isCandidate(value: unknown): value is ManagerialDashboardShareCandidate {
  if (typeof value !== 'object' || value === null) return false
  const item = value as Partial<ManagerialDashboardShareCandidate>
  return typeof item.id === 'number' && typeof item.name === 'string' && typeof item.email === 'string'
}

export type ManagerialDashboardSharesController = {
  shares: ManagerialDashboardShare[]
  candidates: ManagerialDashboardShareCandidate[]
  search: string
  setSearch: (value: string) => void
  isLoading: boolean
  isSearching: boolean
  isSaving: boolean
  error: string | null
  success: string | null
  grantAccess: (institutionId: number, expiresOn: string) => Promise<boolean>
  updateExpiry: (shareId: number, expiresOn: string) => Promise<boolean>
  revokeAccess: (shareId: number) => Promise<boolean>
}

export function useManagerialDashboardShares(isOpen: boolean): ManagerialDashboardSharesController {
  const [shares, setShares] = useState<ManagerialDashboardShare[]>([])
  const [candidates, setCandidates] = useState<ManagerialDashboardShareCandidate[]>([])
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const sharesRequestRef = useRef<AbortController | null>(null)

  const loadShares = useCallback(async () => {
    sharesRequestRef.current?.abort()
    const controller = new AbortController()
    sharesRequestRef.current = controller
    const items = await fetchManagerialDashboardShares(controller.signal)
    if (controller.signal.aborted) return
    setShares(items.filter(isShare))
  }, [])

  useEffect(() => {
    if (!isOpen) {
      setSearch('')
      setError(null)
      setSuccess(null)
      return
    }

    let cancelled = false

    async function load() {
      setIsLoading(true)
      setError(null)
      try {
        await loadShares()
      } catch (err) {
        if (cancelled || isAbortError(err)) return
        setError(SHARE_LOAD_ERROR)
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    void load()

    return () => {
      cancelled = true
      sharesRequestRef.current?.abort()
    }
  }, [isOpen, loadShares])

  useEffect(() => {
    if (!isOpen) return

    const controller = new AbortController()
    let cancelled = false
    const timer = window.setTimeout(() => {
      setIsSearching(true)
      void fetchManagerialDashboardShareCandidates(search, controller.signal)
        .then((items) => {
          if (cancelled) return
          setCandidates(items.filter(isCandidate))
        })
        .catch((err: unknown) => {
          if (cancelled || isAbortError(err)) return
          setError(SHARE_LOAD_ERROR)
        })
        .finally(() => {
          if (!cancelled) {
            setIsSearching(false)
          }
        })
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      cancelled = true
      controller.abort()
      window.clearTimeout(timer)
    }
  }, [isOpen, search])

  const grantAccess = useCallback(
    async (institutionId: number, expiresOn: string): Promise<boolean> => {
      setIsSaving(true)
      setError(null)
      setSuccess(null)
      try {
        await grantManagerialDashboardShare({ institutionId, expiresOn })
        await loadShares()
        setSuccess(SHARE_SAVE_SUCCESS)
        return true
      } catch (err) {
        setError(
          err instanceof ApiClientError
            ? resolveManagerialDashboardError(err)
            : SHARE_LOAD_ERROR
        )
        return false
      } finally {
        setIsSaving(false)
      }
    },
    [loadShares]
  )

  const updateExpiry = useCallback(
    async (shareId: number, expiresOn: string): Promise<boolean> => {
      setIsSaving(true)
      setError(null)
      setSuccess(null)
      try {
        await updateManagerialDashboardShareExpiry({ shareId, expiresOn })
        await loadShares()
        setSuccess(SHARE_SAVE_SUCCESS)
        return true
      } catch (err) {
        setError(resolveManagerialDashboardError(err))
        return false
      } finally {
        setIsSaving(false)
      }
    },
    [loadShares]
  )

  const revokeAccess = useCallback(
    async (shareId: number): Promise<boolean> => {
      setIsSaving(true)
      setError(null)
      setSuccess(null)
      try {
        await revokeManagerialDashboardShare(shareId)
        await loadShares()
        setSuccess(SHARE_REVOKE_SUCCESS)
        return true
      } catch (err) {
        setError(resolveManagerialDashboardError(err))
        return false
      } finally {
        setIsSaving(false)
      }
    },
    [loadShares]
  )

  return {
    shares,
    candidates,
    search,
    setSearch,
    isLoading,
    isSearching,
    isSaving,
    error,
    success,
    grantAccess,
    updateExpiry,
    revokeAccess,
  }
}
